// Customer session token (Phase 2 accounts). Stored in localStorage; sent as a
// Bearer header on authenticated calls. The shared storage helpers make
// private-mode or disabled storage degrade to "signed out" rather than
// duplicating subtly different try/catch wrappers here.

import { API_URL } from "../constants";
import { storageGet, storageRemove, storageSet } from "./storage";

const TOKEN_KEY = "liq_token";
// Only failed storage writes use memory; normal tab storage remains authoritative.
const memoryFunnelTokens = new Map();

export const getToken = () => storageGet(TOKEN_KEY, "local");

/** @param {string} token */
export const setToken = (token) => {
  storageSet(TOKEN_KEY, token, "local");
};

export const clearToken = () => {
  storageRemove(TOKEN_KEY, "local");
};

/**
 * Sign out for real: revoke the session server-side, then clear it locally.
 *
 * Clearing only the local copy leaves the signed 30-day token valid on the
 * server — "Sign out" on a shared machine did nothing for anyone who had
 * copied the token. The revoke is best-effort (fire-and-forget): losing the
 * network must never trap someone on a page they asked to leave, and the
 * local clear below never waits on it.
 */
export const signOutEverywhere = () => {
  const token = getToken();
  if (token) {
    // fetch, not axios: no interceptors, no import cycle, and keepalive lets
    // the request survive the navigation that follows.
    fetch(`${API_URL}/auth/logout/`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => {});
  }
  clearToken();
};

/** @returns {Record<string, string>} Authorization header for axios, or {} when signed out. */
export const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** @param {string} listingUuid */
const funnelKey = (listingUuid) => `liq_funnel:${listingUuid}`;

/** @param {string} listingUuid @param {string} token Keep a capability in this tab only. */
export const setFunnelToken = (listingUuid, token) => {
  if (!listingUuid || !token) return;
  storageSet(funnelKey(listingUuid), token);
  if (storageGet(funnelKey(listingUuid)) === token) memoryFunnelTokens.delete(listingUuid);
  else memoryFunnelTokens.set(listingUuid, token);
};

/** @param {string} listingUuid */
export const getFunnelToken = (listingUuid) => {
  if (!listingUuid) return null;
  return memoryFunnelTokens.get(listingUuid) || storageGet(funnelKey(listingUuid)) || null;
};

/** @param {string} listingUuid */
export const clearFunnelToken = (listingUuid) => {
  if (!listingUuid) return;
  storageRemove(funnelKey(listingUuid));
  memoryFunnelTokens.delete(listingUuid);
};

/**
 * @param {string} listingUuid
 * @returns {Record<string, string>}
 */
export const listingAuthHeader = (listingUuid) => {
  const owner = authHeader();
  const funnelToken = getFunnelToken(listingUuid);
  return {
    ...owner,
    ...(funnelToken ? { "X-Listino-Funnel-Token": funnelToken } : {}),
  };
};
